import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const Massage = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const pricingOptions = [
    {
      title: 'Swedish Massage',
      prices: [
        { duration: '30 min', price: '$40' },
        { duration: '60 min', price: '$80' }
      ]
    },
    {
      title: 'Deep Tissue Massage',
      prices: [
        { duration: '30 min', price: '$50' },
        { duration: '60 min', price: '$100' }
      ]
    },
    {
      title: 'Cupping',
      prices: [
        { duration: 'Session', price: '$100' }
      ]
    }
  ];

  const benefits = [
    {
      title: 'Reduces Muscle Tension & Pain',
      description: 'Alleviates chronic muscle tension, knots, and pain through targeted massage techniques and pressure.'
    },
    {
      title: 'Improves Circulation',
      description: 'Enhances blood flow throughout the body, promoting better oxygen and nutrient delivery to tissues.'
    },
    {
      title: 'Decreases Stress & Anxiety',
      description: 'Promotes deep relaxation, reduces cortisol levels, and calms the nervous system for mental wellness.'
    },
    {
      title: 'Enhances Flexibility & Range of Motion',
      description: 'Loosens tight muscles and improves joint mobility for better movement and athletic performance.'
    },
    {
      title: 'Promotes Better Sleep',
      description: 'Reduces tension and promotes relaxation, leading to improved sleep quality and deeper rest.'
    },
    {
      title: 'Boosts Immune Function',
      description: 'Stimulates lymphatic drainage and improves circulation to support healthy immune system function.'
    },
    {
      title: 'Relieves Headaches & Migraines',
      description: 'Reduces tension in neck, shoulders, and head that often triggers tension headaches and migraines.'
    },
    {
      title: 'Accelerates Recovery',
      description: 'Speeds muscle recovery after exercise or injury by reducing inflammation and promoting healing.'
    },
    {
      title: 'Improves Posture',
      description: 'Releases tight muscles and realigns the body, helping correct postural imbalances and discomfort.'
    },
    {
      title: 'Releases Toxins',
      description: 'Cupping therapy draws stagnant blood and toxins to the surface, promoting detoxification and healing.'
    },
    {
      title: 'Customizable Pressure',
      description: 'Treatments are tailored to your comfort level and specific needs, from gentle relaxation to deep therapeutic work.'
    },
    {
      title: 'Holistic Wellness',
      description: 'Addresses physical, mental, and emotional wellbeing for comprehensive health benefits.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Massage Therapy - Swedish, Deep Tissue & Cupping | Century Aesthetics</title>
        <meta name="description" content="Therapeutic massage including Swedish, deep tissue, and cupping therapy. Relieve pain and stress in Forest Hills, NY." />
        <meta name="keywords" content="massage therapy, deep tissue massage, swedish massage, cupping therapy, therapeutic massage" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/massage" />
        
        <meta property="og:title" content="Massage Therapy | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Therapeutic massage including Swedish, deep tissue, and cupping therapy. Relieve pain and stress in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/massage" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/massage.webp" 
                  alt="Massage Therapy - Swedish, Deep Tissue, Cupping" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                THERAPEUTIC MASSAGE: SWEDISH, DEEP TISSUE & CUPPING THERAPY
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Our comprehensive massage therapy services provide relief from muscle tension, chronic pain, and stress through expertly performed Swedish massage, Deep Tissue massage, and Cupping therapy. Swedish massage uses gentle, flowing strokes to promote relaxation, improve circulation, and reduce stress—perfect for those seeking overall wellness and tension relief. Deep Tissue massage applies focused pressure to reach deeper muscle layers and fascia, effectively treating chronic pain, muscle knots, and sports injuries. Both modalities are customized to your comfort level and specific therapeutic needs.
                </p>
                <p>
                  Cupping therapy offers additional healing benefits by using suction cups to draw stagnant blood and toxins to the skin's surface, promoting detoxification, reducing inflammation, and accelerating recovery. All massage services are performed by licensed massage therapists in a tranquil, comfortable setting. Choose from 30-minute or 60-minute sessions based on your schedule and treatment goals. Regular massage therapy supports pain management, stress reduction, improved mobility, and overall wellness. Schedule your therapeutic massage at Century Longevity & Aesthetics to experience professional healing and relaxation.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Massage Therapy Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto mb-6"></div>
       
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-8 shadow-xl text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center">
                  <svg 
                    className="w-10 h-10 text-gold" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1.5} 
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                    />
                  </svg>
                </div>
                
                <h3 className="text-xl font-heading font-semibold text-olive-darkest mb-4 uppercase tracking-wide">
                  {option.title}
                </h3>
                
                <div className="space-y-3 mb-6">
                  {option.prices.map((priceInfo, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-sm font-body text-gray-600">{priceInfo.duration}:</span>
                      <span className="text-2xl font-heading font-light text-gold">{priceInfo.price}</span>
                    </div>
                  ))}
                </div>
                
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full bg-olive-darkest text-white px-6 py-3 rounded-lg font-medium text-sm tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
                >
                  Book Now
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Massage Therapy</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Experience the healing power of professional massage therapy. Reduce pain, release tension, and restore balance to body and mind!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about massage therapy options, what to expect during your session, and which treatment is best for your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('massage');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Massage;